import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotificationAsync,
  fetchUnreadCount,
  resetNotifications,
} from "@/store/slices/notifications.slice";
import { useParams } from "react-router-dom";

export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const storeId = params.storeId as string;

  const { notifications = [], unreadCount = 0, loading = false, error = null, page = 1, hasMore = true } =
    useAppSelector((state) => state.notifications || {});

  // Initial fetch
  const refreshNotifications = useCallback(() => {
    if (storeId) {
      dispatch(resetNotifications());
      dispatch(fetchNotifications({ page: 1, limit: 20 }));
    }
  }, [dispatch, storeId]);

  // Load more notifications (pagination)
  const loadMore = useCallback(() => {
    if (storeId && !loading && hasMore) {
      dispatch(fetchNotifications({ page: page + 1, limit: 20 }));
    }
  }, [dispatch, storeId, loading, hasMore, page]);

  // Mark single notification as read/unread
  const markAsRead = useCallback(
    (notificationId: string, read: boolean = true) => {
      dispatch(markNotificationAsRead({ notificationId, read }));
    },
    [dispatch]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    dispatch(markAllNotificationsAsRead());
  }, [dispatch]);

  // Delete a notification
  const deleteNotification = useCallback(
    (notificationId: string) => {
      dispatch(deleteNotificationAsync(notificationId));
    },
    [dispatch]
  );

  // Update unread count
  const updateUnreadCount = useCallback(() => {
    if (storeId) {
      dispatch(fetchUnreadCount());
    }
  }, [dispatch, storeId]);

  // Auto-fetch unread count on mount
  useEffect(() => {
    if (storeId) {
      updateUnreadCount();
    }
  }, [storeId, updateUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    refreshNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updateUnreadCount,
  };
};
